import { computed, ref, watch } from 'vue'
import { VALID_MULTIPLE_SELECT_BUTTON_TYPES, DEFAULT_PROPS, FIELD_MAPPING } from '../constants/multiple-select-button-constants.js'

/**
 * Core logic for Multiple Select Button component
 * Handles state management, selection cache, and model value synchronization
 */
export function useMultipleSelectButton(props) {
  
  // State management
  const internalText = ref('')
  const cacheSelected = ref(new Map())
  const toggleMenu = ref(false)
  
  // Basic state computations - giữ nguyên logic từ file gốc
  const isDisabled = computed(() => Boolean(props.disabled))
  
  // Content checks
  const hasText = computed(() => Boolean(props.text))
  const hasLeftIcon = computed(() => Boolean(props.leftIcon))
  const hasRightIcon = computed(() => Boolean(props.rightIcon))
  const hasData = computed(() => Array.isArray(props.data) && props.data.length > 0)
  
  // Validation
  const isValidType = computed(() => VALID_MULTIPLE_SELECT_BUTTON_TYPES.includes(props.type))
  
  // Border states - giữ nguyên logic từ file gốc
  const shouldShowBorder = computed(() => Boolean(props.hasBorder))
  const shouldShowParentBorder = computed(() => Boolean(props.hasBorderParent))
  
  // Menu positioning and sizing - giữ nguyên logic từ file gốc
  const hasCustomPosition = computed(() => Boolean(props.customPositionMenu))
  const customMenuWidth = computed(() => props.customWidth || DEFAULT_PROPS.customWidth)
  const menuHeightOffset = computed(() => props.heightOffset || DEFAULT_PROPS.heightOffset)
  const menuWidthOffset = computed(() => props.widthOffset || DEFAULT_PROPS.widthOffset)
  
  // Show menu state
  const shouldShowMenu = computed(() => Boolean(props.hasShowMenu))
  
  // Tooltip state
  const hasTooltip = computed(() => Boolean(props.tooltipText))
  
  // Field mapping - giữ nguyên logic từ file gốc
  const effectiveValueField = computed(() => props.valueField || FIELD_MAPPING.VALUE_FIELD)
  const effectiveDisplayField = computed(() => props.displayField || FIELD_MAPPING.DISPLAY_FIELD)
  
  // Selection state
  const selectedItems = computed(() => Array.from(cacheSelected.value.values()))
  const selectedCount = computed(() => cacheSelected.value.size)
  const hasSelectedItems = computed(() => selectedCount.value > 0)
  
  // Watch modelValue changes - giữ NGUYÊN logic từ file gốc
  watch(
    () => props.modelValue,
    (value, oldValue) => {
      if (value !== null && value !== internalText.value) {
        internalText.value = value
        const splitOptions = internalText.value.split(';')
        cacheSelected.value.clear()
        let tmpCacheData = new Map()
        props.data.forEach(item => {
          tmpCacheData.set(item[effectiveDisplayField.value], item)
        })

        splitOptions.forEach(item => {
          if (tmpCacheData.has(item)) {
            const curItem = tmpCacheData.get(item)
            cacheSelected.value.set(curItem[effectiveValueField.value], curItem)
          }
        })
      }
    },
    { immediate: true }
  )
  
  // Helper functions for selection management
  const isItemSelected = (item) => {
    return cacheSelected.value.has(item[effectiveValueField.value])
  }
  
  const selectItem = (item) => {
    if (!cacheSelected.value.has(item[effectiveValueField.value])) {
      cacheSelected.value.set(item[effectiveValueField.value], item)
    }
  }
  
  const deselectItem = (item) => {
    if (cacheSelected.value.has(item[effectiveValueField.value])) {
      cacheSelected.value.delete(item[effectiveValueField.value])
    }
  }
  
  const toggleItemSelection = (item) => {
    if (isItemSelected(item)) {
      deselectItem(item)
    } else {
      selectItem(item)
    }
  }
  
  const clearSelection = () => {
    cacheSelected.value.clear()
  }
  
  return {
    // State
    isDisabled,
    internalText,
    cacheSelected,
    toggleMenu,
    
    // Content checks
    hasText,
    hasLeftIcon, 
    hasRightIcon,
    hasData,
    
    // Validation
    isValidType,
    
    // Border states
    shouldShowBorder,
    shouldShowParentBorder,
    
    // Menu positioning
    hasCustomPosition,
    customMenuWidth,
    menuHeightOffset,
    menuWidthOffset,
    shouldShowMenu,
    
    // Tooltip
    hasTooltip,
    
    // Field mapping
    effectiveValueField,
    effectiveDisplayField,
    
    // Selection state
    selectedItems,
    selectedCount,
    hasSelectedItems,
    
    // Selection methods
    isItemSelected,
    selectItem,
    deselectItem,
    toggleItemSelection,
    clearSelection
  }
}
